import { defineStore } from 'pinia';

interface ILivechatState {
  newMessages: number;
  initialize: boolean;
  scriptAdded: boolean;
}

export const useLivechatStore = defineStore('livechatStore', {
  state: (): ILivechatState => ({
    newMessages: 0,
    initialize: true,
    scriptAdded: false,
  }),

  getters: {
    projectHasLivechat() {
      if (!window) return undefined;
      const { public: { livechatParams } } = useRuntimeConfig();
      return !!(livechatParams?.license);
    },
  },

  actions: {
    addLiveChatScript() {
      if (!this.projectHasLivechat) {
        return;
      }
      if (this.scriptAdded) {
        this.initChat();
        return;
      }

      this.scriptAdded = true;
      this.initChat();
    },

    getProfileData() {
      const { profile } = useProfileStore();
      const { activePlayerBonuses, activePlayerFreeSpins } = useBonusStore();

      return {
        name: `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim(),
        email: profile?.email || '',
        customFields: {
          cf_segments: '', // TODO: get player segments from API
          cf_active_bonuses: `${!!(activePlayerBonuses?.length || activePlayerFreeSpins?.length)}`,
        },
      };
    },

    async updateLivechatUser(): Promise<void> {
      if (!window.LC_API) return;
      const { profile } = useProfileStore();

      if (profile) {
        try {
          const profileData = this.getProfileData();
          
          // Set customer email if method exists
          if (typeof window.LC_API.set_customer_email === 'function') {
            window.LC_API.set_customer_email(profileData.email);
          }
          
          // Set customer name if method exists
          if (typeof window.LC_API.set_customer_name === 'function') {
            window.LC_API.set_customer_name(profileData.name);
          }
          
          // Set custom fields if available
          if (profileData.customFields && typeof window.LC_API.set_custom_field === 'function') {
            const lcApi = window.LC_API;
            Object.entries(profileData.customFields).forEach(([key, value]) => {
              lcApi.set_custom_field(key, value);
            });
          }
        } catch {
          console.log('LiveChat user not updated!');
        }
      }
    },

    setUserChatData(): void {
      const { isLoggedIn } = useProfileStore();

      if (isLoggedIn) {
        this.updateLivechatUser();

        useListen('profileUpdated', this.updateLivechatUser);
        useListen('freeSpinsUpdated', this.updateLivechatUser);
        useListen('bonusesUpdated', this.updateLivechatUser);
        useListen('accountChanged', this.updateLivechatUser);
      }
    },

    initChat() {
      const { public: { livechatParams } } = useRuntimeConfig();

      if (!this.initialize || !livechatParams?.license) {
        return;
      }
      
      this.initialize = false;

      // Wait for LC_API to be available with multiple retries
      let retryCount = 0;
      const maxRetries = 20;
      
      const checkAndInit = () => {
        // Check if LC_API is available and has the required methods
        if (window.LC_API && typeof window.LC_API.open_chat_window === 'function') {
          this.setUserChatData();
          
          // Listen for unread messages - only if method exists
          if (typeof window.LC_API.on_chat_loaded === 'function') {
            window.LC_API.on_chat_loaded = () => {
              this.setUserChatData();
            };
          }

          if (typeof window.LC_API.on_message === 'function') {
            window.LC_API.on_message = () => {
              this.updateUnreadCount();
            };
          }

          if (typeof window.LC_API.on_chat_window_opened === 'function') {
            window.LC_API.on_chat_window_opened = () => {
              this.newMessages = 0;
            };
          }
        } else {
          retryCount++;
          if (retryCount < maxRetries) {
            setTimeout(checkAndInit, 1000);
          } else {
            console.error('LiveChat: Failed to initialize after maximum retries');
            this.initialize = true;
          }
        }
      };

      // Start checking after a delay to allow widget to load
      setTimeout(checkAndInit, 2000);
    },

    updateUnreadCount() {
      // Check if chat window is minimized using available methods
      if (window.LC_API) {
        try {
          // Try different methods to check chat window state
          if (typeof window.LC_API.get_chat_window_state === 'function') {
            if (window.LC_API.get_chat_window_state() === 'minimized') {
              this.newMessages++;
            }
          } else if (typeof (window.LC_API as any).get_chat_window_state === 'function') {
            if ((window.LC_API as any).get_chat_window_state() === 'minimized') {
              this.newMessages++;
            }
          } else if ((window as any).LiveChatWidget) {
            // Use LiveChatWidget API to check state
            (window as any).LiveChatWidget.get('chat_window_state').then((state: string) => {
              if (state === 'minimized') {
                this.newMessages++;
              }
            });
          } else {
            // If we can't determine state, increment anyway
            this.newMessages++;
          }
        } catch (error) {
          console.log('LiveChat: Error checking chat window state:', error);
          this.newMessages++;
        }
      }
    },

    openChat() {
      const openChatWindow = () => {
        if (window.LC_API) {
          // Try different methods to open chat
          if (typeof window.LC_API.open_chat_window === 'function') {
            window.LC_API.open_chat_window();
            this.newMessages = 0;
          } else if (window.LC_API.open_chat_window && typeof (window.LC_API.open_chat_window as any).open === 'function') {
            (window.LC_API.open_chat_window as any).open();
            this.newMessages = 0;
          } else if (typeof (window.LC_API as any).open === 'function') {
            (window.LC_API as any).open();
            this.newMessages = 0;
          } else if ((window.LC_API as any).trigger) {
            (window.LC_API as any).trigger('open_chat_window');
            this.newMessages = 0;
          } else if ((window as any).LiveChatWidget) {
            (window as any).LiveChatWidget.call('maximize');
            this.newMessages = 0;
          } else {
            console.error('LiveChat: No available method to open chat window');
            
            // Try to find any method that might open the chat
            const possibleMethods = ['open', 'show', 'display', 'start', 'launch'];
            for (const method of possibleMethods) {
              if (typeof (window.LC_API as any)[method] === 'function') {
                try {
                  (window.LC_API as any)[method]();
                  this.newMessages = 0;
                  break;
                } catch (error) {
                  console.log(`LiveChat: ${method} method failed:`, error);
                }
              }
            }
          }
        } else {
          setTimeout(openChatWindow, 500);
        }
      };
      
      openChatWindow();
    },

    async updateChat(): Promise<void> {
      const { public: { livechatParams } } = useRuntimeConfig();
      if (!livechatParams?.license || !window.LC_API) return;

      this.initialize = true;

      try {
        // Clear user data when logging out using available methods
        if (typeof window.LC_API.set_customer_email === 'function') {
          window.LC_API.set_customer_email('');
        }
        if (typeof window.LC_API.set_customer_name === 'function') {
          window.LC_API.set_customer_name('');
        }
      } catch {
        console.log('LiveChat not cleared, chat empty!');
      }

      useUnlisten('profileUpdated');
      useUnlisten('freeSpinsUpdated');
      useUnlisten('bonusesUpdated');
      useUnlisten('accountChanged');
      
      this.newMessages = 0;
    },
  },
}); 